import dayjs from 'dayjs';
import round from 'lodash/round';

import { SxProps, type InputProps } from '@cast/design-system';
import {
  HPAScalingPolicyType,
  HPAUnsupportedReasonTypeEnum,
  OptimizationWorkload,
  UpdateVerticalWorkloadScaling,
  WorkloadEventType,
  WorkloadOptimizationHorizontalPodAutoscalerBehavior,
  WorkloadOptimizationMetricSpec,
  WorkloadOptimizationResourceConfigFunction,
  WorkloadOptimizationState,
  WorkloadOverrides,
  WorkloadResourceConfig,
  WorkloadRolloutBehaviorSettings,
  WorkloadScalingAntiAffinitySettings,
  WorkloadScalingPolicy,
  WorkloadScalingPolicyConfig,
  WorkloadScalingRecommendationPolicies,
  WorkloadScalingResourceLimitStrategy,
  WorkloadStartupSettings,
  WorkloadConstraintsSettings,
} from '@cast/types';
import { convertUnit, toBig } from '@cast/utils';

import {
  computeResourceNameMap,
  computeResourceUnitsMap,
} from 'constants/maps';
import {
  OptimizationType,
  PreparedWorkload,
} from 'features/workload-optimization/types';
import { isManagedByAnnotations } from 'features/workload-optimization/utils';
import { type PartialWorkloadUpdateRequest } from 'hooks/mutations/workload-optimization/useUpdateWorkloadV2Mutation';

import {
  maxLookbackPeriodSeconds,
  OPTIMIZATION_TYPE_TO_SEPARATE_OPTIONS_MAP,
} from './_constants';
import {
  HpaDisableReasons,
  OptimizationThresholdType,
  PolicyAutoscalerFormMode,
  ResourcesConstraintsV2,
  StartupMetricsFormModel,
  WorkloadResourceLimitFormModel,
} from './_types';
import { defaultScalingPolicyFormModel } from './scaling-policy-details/form/_constants';
import {
  ResourceMetric,
  ScalingPolicyEvent,
  WorkloadOptimizationAutoscalerBehaviorFormModel,
  WorkloadOptimizationMetricFormModel,
} from './types';

import {
  DAY_HOUR_PRESETS,
  EVICTION_DELAY_PRESETS,
  MINUTE_SECOND_PRESETS,
} from '@cast/shared-rff-ui';

/** Events API rejects toDate in the future (calendar end-of-day can be after now). */
export const clampEventsToDateToNow = (toDate?: string): string | undefined => {
  if (!toDate) {
    return toDate;
  }
  const now = dayjs.utc();
  return dayjs.utc(toDate).isAfter(now) ? now.toISOString() : toDate;
};

export const toGibOrNull = (val?: number | null): number | null =>
  val ? round(convertUnit(val, 'MiB', 'GiB'), 2) : null;

export const toMibOrNull = (val: number | null): number | null =>
  val ? convertUnit(val, 'GiB', 'MiB') : null;

export const getLookbackDaysHours = ({
  lookBackPeriodSeconds: seconds,
}: Pick<WorkloadResourceConfig, 'lookBackPeriodSeconds'> = {}): {
  days: number;
  hours: number;
  isCustom: boolean;
} => {
  if (!seconds) {
    return { days: 1, hours: 0, isCustom: false };
  }
  const days = Math.floor(seconds / (24 * 60 * 60));
  const hours = Math.floor((seconds % (24 * 60 * 60)) / (60 * 60));
  const isCustom = !DAY_HOUR_PRESETS.some(
    (p) => (p.days || 0) === days && (p.hours || 0) === hours
  );
  return { days, hours, isCustom };
};

export const formatLookbackPeriod = (
  resourceConfig: WorkloadResourceConfig
): string => {
  const { days, hours } = getLookbackDaysHours(resourceConfig);

  return `${days}d ${hours}h`;
};

export const getRightsizingManagementOption = (
  enabled: boolean
): WorkloadOptimizationState =>
  enabled
    ? WorkloadOptimizationState.MANAGED
    : WorkloadOptimizationState.READ_ONLY;

export const isWorkloadResourceOptimized = (
  resource: WorkloadResourceConfig
): boolean => resource.managementOption !== WorkloadOptimizationState.READ_ONLY;
export const getRecommendationPercentile = (
  config: WorkloadResourceConfig
): number => {
  const fn = config.function;
  if (fn === WorkloadOptimizationResourceConfigFunction.MAX) {
    return 100;
  }
  try {
    return toBig(config.args[0]).mul(100).toNumber();
  } catch {
    return 0;
  }
};

export const getRestartPodsOneByOne = (
  rolloutBehavior: WorkloadRolloutBehaviorSettings | undefined
): boolean => !!rolloutBehavior?.preferOneByOne;

export const getOverhead = ({ overhead }: WorkloadResourceConfig): number =>
  (overhead || 0) * 100;

export const minMaxFormatter = ({
  min,
  max,
}: {
  min: number | null;
  max: number | null;
}): string => `${min ?? '--'}/${max ?? '--'}`;

export const getOptimizationThresholdFormModel = (
  config: WorkloadResourceConfig['applyThresholdStrategy']
): {
  percentage: number;
  type: OptimizationThresholdType;
} => {
  return {
    percentage: (config?.percentageThreshold?.percentage || 0) * 100,
    type: config?.defaultAdaptiveThreshold
      ? OptimizationThresholdType.DYNAMIC
      : OptimizationThresholdType.PERCENTAGE,
  };
};

export const getStallDetectionFormModel = (
  anomalyDetection: WorkloadScalingRecommendationPolicies['anomalyDetection']
): {
  cpuStallThresholdPercentage: number;
  minPressuredPodPercentage: number;
} => {
  //  todo(mike) will never be non required, need to fix types after api is updated
  return {
    cpuStallThresholdPercentage:
      anomalyDetection?.cpuPressure?.cpuStallThresholdPercentage ??
      defaultScalingPolicyFormModel.vpa.stallDetection
        .cpuStallThresholdPercentage,
    minPressuredPodPercentage:
      anomalyDetection?.cpuPressure?.minPressuredPodPercentage ??
      defaultScalingPolicyFormModel.vpa.stallDetection
        .minPressuredPodPercentage,
  };
};

export const getResourceLimitsCpu = (
  config: WorkloadResourceConfig['limit']
): WorkloadResourceLimitFormModel => {
  // !limit -> NO_LIMIT (Default)
  // limit.type === 'UNKNOWN' -> NO_LIMIT
  // limit.type === 'MULTIPLIER' -> MULTIPLIER or SEMI_AUTOMATIC
  // limit.type === 'NO_LIMIT' -> NO_LIMIT
  // limit.type === 'KEEP_LIMITS' -> KEEP_LIMITS
  // limit.type === 'MAINTAIN_RATIO' -> MAINTAIN_RATIO

  if (!config?.type || config.type === 'UNKNOWN') {
    return {
      type: 'NO_LIMIT',
      multiplier: config?.multiplier ?? null,
      semiAutoMultiplier: null,
    };
  }

  if (
    config.type === 'MULTIPLIER' &&
    config.onlyIfOriginalExist &&
    config.onlyIfOriginalLower
  ) {
    return {
      type: 'SEMI_AUTOMATIC',
      multiplier: null,
      semiAutoMultiplier: config.multiplier ?? null,
    };
  }

  return {
    type: config.type,
    multiplier: config.multiplier ?? null,
    semiAutoMultiplier: null,
  };
};

export const getResourceLimitsMemory = (
  config: WorkloadResourceConfig['limit']
): WorkloadResourceLimitFormModel => ({
  // !limit -> AUTO (Default)
  // limit.type === 'UNKNOWN' -> AUTO
  // limit.type === 'MULTIPLIER' -> MULTIPLIER
  // limit.type === 'NO_LIMIT' -> NO_LIMIT
  // limit.type === 'KEEP_LIMITS' -> KEEP_LIMITS
  // limit.type === 'MAINTAIN_RATIO' -> MAINTAIN_RATIO
  type: !config?.type || config.type === 'UNKNOWN' ? 'AUTO' : config.type,
  multiplier: config?.multiplier ?? null,
  semiAutoMultiplier: null,
});

export const getWorkloadCpuConstraints = (
  resource?: Pick<WorkloadResourceConfig, 'constraints'>
): ResourcesConstraintsV2 => {
  const c = resource?.constraints;
  if (c?.min?.percentageOfOriginal || c?.max?.percentageOfOriginal) {
    return {
      mode: 'percentage',
      min: c.min?.percentageOfOriginal?.value ?? null,
      max: c.max?.percentageOfOriginal?.value ?? null,
    };
  }

  return {
    mode: 'constant',
    min: c?.min?.constant?.value ?? null,
    max: c?.max?.constant?.value ?? null,
  };
};

export const getWorkloadMemoryConstraintsV2 = (
  resource?: Pick<WorkloadResourceConfig, 'constraints'>
): ResourcesConstraintsV2 => {
  const c = resource?.constraints;
  if (c?.min?.percentageOfOriginal || c?.max?.percentageOfOriginal) {
    return {
      mode: 'percentage',
      min: c.min?.percentageOfOriginal?.value ?? null,
      max: c.max?.percentageOfOriginal?.value ?? null,
    };
  }

  return {
    mode: 'constant',
    min: toGibOrNull(c?.min?.constant?.value),
    max: toGibOrNull(c?.max?.constant?.value),
  };
};

export const getWorkloadConstraints = ({
  cpu,
  memory,
}: {
  cpu?: Pick<WorkloadResourceConfig, 'constraints'>;
  memory?: Pick<WorkloadResourceConfig, 'constraints'>;
} = {}): { cpu: ResourcesConstraintsV2; memory: ResourcesConstraintsV2 } => {
  return {
    cpu: getWorkloadCpuConstraints(cpu),
    memory: getWorkloadMemoryConstraintsV2(memory),
  };
};

export const secondsToStartupMetricsFormModel = (
  startup?: WorkloadStartupSettings | null
): StartupMetricsFormModel => {
  const seconds = startup?.periodSeconds;
  const hasPeriod = !!seconds;
  const min = hasPeriod ? Math.trunc((seconds || 0) / 60) : 2;
  const sec = (seconds || 0) % 60;
  const isCustom = !MINUTE_SECOND_PRESETS.some(
    (p) => (p.minutes || 0) === min && (p.seconds || 0) === sec
  );

  if (!hasPeriod) {
    return { type: 'no-strategy', min, sec, isCustom };
  }

  if (startup?.twoPhaseRecommendations?.enabled === true) {
    return { type: 'two-phase', min, sec, isCustom };
  }

  return { type: 'ignore-startup', min, sec, isCustom };
};

export const getAntiAffinity = ({
  antiAffinity,
}: Pick<
  WorkloadScalingRecommendationPolicies,
  'antiAffinity'
>): PolicyAutoscalerFormMode =>
  antiAffinity?.considerAntiAffinity === true
    ? 'DEFERRED'
    : antiAffinity?.considerAntiAffinity === false
    ? 'IMMEDIATE'
    : 'DEFERRED';

export const startupMetricsFormModelToSeconds = (
  model: StartupMetricsFormModel
): WorkloadStartupSettings => {
  if (model.type === 'no-strategy') {
    return { periodSeconds: null };
  }
  return {
    // Value cannot be higher that hour
    periodSeconds: Math.min((model.min || 0) * 60 + model.sec, 60 * 60),
    twoPhaseRecommendations: {
      enabled: model.type === 'two-phase',
    },
  };
};

export const canEditWorkload = ({
  isManagedByAnnotations,
  verticalScalingPolicy,
}: PreparedWorkload): boolean => {
  return !isManagedByAnnotations && !verticalScalingPolicy?.isCastware;
};

export const markTop = (markerColor: string): SxProps => ({
  position: 'relative',
  overflow: 'hidden',
  '&:before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: 2,
    backgroundColor: markerColor,
  },
});

export const getWorkloadNativeHpaSpec = (
  workload?: Pick<PreparedWorkload, 'hpaState' | 'hpaSpec'>
): PreparedWorkload['hpaSpec'] => {
  if (workload?.hpaState?.mode === 'HPA_MODE_NATIVE') {
    return workload.hpaState.nativeHpa ?? workload.hpaSpec;
  }

  return workload?.hpaSpec;
};

export const workloadHasNativeHpaSettings = (
  workload: Pick<PreparedWorkload, 'hpaState' | 'hpaSpec'>
): boolean => {
  if (workload.hpaState?.mode) {
    return workload.hpaState.mode === 'HPA_MODE_NATIVE';
  }

  const { hpaSpec } = workload;
  return !!hpaSpec && hpaSpec.managedByCastai === false;
};

export const getHpaDisabledReasons = (
  workload: PreparedWorkload
): Omit<
  HpaDisableReasons,
  'isCastAgentOutdatedForHpa' | 'isWoopAgentOutdatedForHpa' | 'isDisabled'
> => {
  return {
    typeNotSupported: !workload.isTypeSupportedByHpa,
    hasNativeHpa:
      workloadHasNativeHpaSettings(workload) || !!workload.hasNativeHpa,
    managedByAnnotations: isManagedByAnnotations(workload),
    isCastware: !!workload.verticalScalingPolicy?.isCastware,
  };
};

export const workloadOverridesToVpaConfigApiModel = (
  workloadOverrides: WorkloadOverrides | undefined
): UpdateVerticalWorkloadScaling => {
  const vo = workloadOverrides?.vertical;

  if (!vo) {
    return {};
  }

  const result: UpdateVerticalWorkloadScaling = {
    managementOption: vo.managementOption,
  };

  if (vo.cpu) {
    result.cpu = vo.cpu;
  }

  if (vo.memory) {
    result.memory = vo.memory;
  }

  if (vo.containerConstraints) {
    result.containerConfig = vo.containerConstraints;
  }

  if (vo.memoryEvent) {
    result.memoryEvent = vo.memoryEvent;
  }

  if (vo.antiAffinity) {
    result.antiAffinity = vo.antiAffinity;
  }

  return result;
};

export const isPolicyResourceOptimized = ({
  managementOption,
}: WorkloadScalingPolicyConfig): boolean =>
  managementOption !== WorkloadOptimizationState.READ_ONLY;

export const isPolicyGpuOptimized = (
  gpu: WorkloadScalingRecommendationPolicies['gpu']
): boolean => gpu?.managementOption === WorkloadOptimizationState.MANAGED;

export const isPolicyOptimized = ({
  managementOption,
}: WorkloadScalingRecommendationPolicies): boolean =>
  managementOption === WorkloadOptimizationState.MANAGED;

export const isSystemPolicy = ({
  isReadonly,
  isCastware,
}: WorkloadScalingPolicy): boolean => isReadonly && !isCastware;

export const getPolicyManagementOption = (
  enabled: boolean
): WorkloadOptimizationState =>
  enabled
    ? WorkloadOptimizationState.MANAGED
    : WorkloadOptimizationState.READ_ONLY;

export const getWorkloadConstraintsMinInputProps = (
  type: 'cpu' | 'memory'
): Pick<InputProps, 'label' | 'endAdornment'> => ({
  label: `Min. ${computeResourceNameMap[type]}`,
  endAdornment: computeResourceUnitsMap[type],
});

export const getWorkloadConstraintsMaxInputProps = (
  type: 'cpu' | 'memory'
): Pick<InputProps, 'label' | 'endAdornment'> => ({
  label: `Max. ${computeResourceNameMap[type]}`,
  endAdornment: computeResourceUnitsMap[type],
});

export const getDelayMinutesSeconds = (
  totalSeconds?: number | null
): { minutes: number; seconds: number; isCustom: boolean } => {
  if (!totalSeconds) {
    return {
      ...defaultScalingPolicyFormModel.vpa.rolloutBehavior.delaySeconds,
    };
  }
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  const isCustom = !EVICTION_DELAY_PRESETS.some(
    (p) => (p.minutes || 0) === minutes && (p.seconds || 0) === seconds
  );
  return { minutes, seconds, isCustom };
};

export const getDelaySecondsApiValue = ({
  minutes,
  seconds,
}: {
  minutes?: number;
  seconds?: number;
}): number => Math.min((minutes ?? 0) * 60 + (seconds ?? 0), 3600);

// Setting Getters for Api Values
export const getLookbackSecondsApiValue = ({
  days,
  hours,
}: {
  days?: number;
  hours?: number;
}): number | null => {
  if (days == null && hours == null) {
    return null;
  }

  return Math.min(
    (days ?? 0) * 24 * 60 * 60 + (hours ?? 0) * 60 * 60,
    maxLookbackPeriodSeconds
  );
};

export const getAntiAffinityApiValue = (
  value: PolicyAutoscalerFormMode
): WorkloadScalingAntiAffinitySettings => ({
  considerAntiAffinity: value === 'DEFERRED', // true for DEFERRED and false for IMMEDIATE
});

export const getConstraintsApiValues = (
  wc: ResourcesConstraintsV2,
  transformMinMax: (v: number | null) => number | null = (v) => v
): { constraints: WorkloadConstraintsSettings } => {
  if (wc.mode === 'percentage') {
    return {
      constraints: {
        min:
          wc.min !== null
            ? { percentageOfOriginal: { value: wc.min } }
            : undefined,
        max:
          wc.max !== null
            ? { percentageOfOriginal: { value: wc.max } }
            : undefined,
      },
    };
  }
  const minValue =
    wc.min === null ? undefined : transformMinMax(wc.min) ?? wc.min;
  const maxValue =
    wc.max === null ? undefined : transformMinMax(wc.max) ?? wc.max;

  return {
    constraints: {
      min:
        minValue !== undefined ? { constant: { value: minValue } } : undefined,
      max:
        maxValue !== undefined ? { constant: { value: maxValue } } : undefined,
    },
  };
};

export const getResourceLimitApiValue = ({
  multiplier,
  semiAutoMultiplier,
  type,
}: WorkloadResourceLimitFormModel):
  | (Omit<WorkloadScalingResourceLimitStrategy, 'type'> & {
      type: 'MULTIPLIER' | 'NO_LIMIT' | 'KEEP_LIMITS' | 'MAINTAIN_RATIO';
    })
  | undefined =>
  type === 'AUTO'
    ? undefined
    : type === 'SEMI_AUTOMATIC'
    ? {
        type: 'MULTIPLIER',
        multiplier: semiAutoMultiplier!,
        onlyIfOriginalExist: true,
        onlyIfOriginalLower: true,
      }
    : {
        type,
        multiplier:
          type === 'NO_LIMIT' ||
          type === 'KEEP_LIMITS' ||
          type === 'MAINTAIN_RATIO'
            ? undefined
            : multiplier ?? undefined, // null to undefined
      };

export const getScalingPolicyFromEvent = (
  event: ScalingPolicyEvent
): WorkloadScalingPolicy => {
  switch (event.type) {
    case WorkloadEventType.SCALING_POLICY_UPDATED: {
      return event.details.current;
    }
    default:
      return event.details.policy;
  }
};

export const isWorkloadOptimized = (workload: PreparedWorkload): boolean =>
  workload.isVerticalScalingEnabled || workload.isHorizontalScalingEnabled;

// ---- Predictive ----
export const isPredictiveWorkloadEligible = (
  workload: PreparedWorkload
): boolean => workload.predictionInsights.isEligible;

/**
 * Config can stay enabled after a workload loses eligibility.
 * The UI treats that as off so counts and controls match the eligible set.
 */
export const isPredictiveWorkloadEnabled = (
  workload: PreparedWorkload
): boolean =>
  isPredictiveWorkloadEligible(workload) &&
  !!workload.workloadConfigV2.vpaConfig.predictiveScaling?.cpu?.enabled;

export const canEnablePredictiveWorkload = (
  workload: PreparedWorkload
): boolean =>
  isPredictiveWorkloadEligible(workload) &&
  !isPredictiveWorkloadEnabled(workload);

export const hasPredictiveWorkloads = (
  workloads: PreparedWorkload[]
): boolean =>
  workloads.some((workload) => isPredictiveWorkloadEligible(workload));

export const getWorkloadEnablePredictiveScalingPatchBody = ({
  workloadId,
  enabled,
  clusterId,
}: {
  workloadId: string;
  clusterId: string;
  enabled: boolean;
}): PartialWorkloadUpdateRequest => ({
  clusterId,
  workloadId,
  workload: {
    workloadConfig: {
      vpaConfig: {
        predictiveScaling: {
          cpu: {
            enabled,
          },
        },
      },
    },
  },
});

export const getOptimizationTypeFromScalingPolicy = ({
  vpaManagementOption,
  hpaManagementOption,
}: {
  vpaManagementOption?: WorkloadOptimizationState;
  hpaManagementOption?: WorkloadOptimizationState;
}): OptimizationType => {
  const vpaEnabled = vpaManagementOption === WorkloadOptimizationState.MANAGED;
  const hpaEnabled = hpaManagementOption === WorkloadOptimizationState.MANAGED;

  return (Object.entries(OPTIMIZATION_TYPE_TO_SEPARATE_OPTIONS_MAP).find(
    ([, value]) =>
      value.vpaEnabled === vpaEnabled && value.hpaEnabled === hpaEnabled
  )?.[0] ?? OptimizationType.OFF) as OptimizationType;
};

const isResourceMetric = (
  metric: WorkloadOptimizationMetricSpec
): metric is ResourceMetric => {
  return metric.type === 'RESOURCE';
};

export const mapApiMetricsToFormModel = (
  metrics: WorkloadOptimizationMetricSpec[]
): WorkloadOptimizationMetricFormModel => {
  const cpuMetric = metrics.find(
    (m): m is ResourceMetric => isResourceMetric(m) && m.resource.name === 'cpu'
  );
  const memoryMetric = metrics.find(
    (m): m is ResourceMetric =>
      isResourceMetric(m) && m.resource.name === 'memory'
  );

  return {
    cpu: cpuMetric ? Number(cpuMetric.resource.target.value) : null,
    memory: memoryMetric ? Number(memoryMetric.resource.target.value) : null,
  };
};

export const mapFormModelMetricsToApi = (
  metrics: WorkloadOptimizationMetricFormModel
): WorkloadOptimizationMetricSpec[] => {
  const result: WorkloadOptimizationMetricSpec[] = [];

  if (metrics.cpu !== null) {
    result.push({
      type: 'RESOURCE',
      resource: {
        name: 'cpu',
        target: { type: 'UTILIZATION', value: String(metrics.cpu) },
      },
    });
  }

  if (metrics.memory !== null) {
    result.push({
      type: 'RESOURCE',
      resource: {
        name: 'memory',
        target: { type: 'UTILIZATION', value: String(metrics.memory) },
      },
    });
  }

  return result;
};

export const mapApiAutoscalerBehaviorToFormModel = (
  behavior?: WorkloadOptimizationHorizontalPodAutoscalerBehavior
): WorkloadOptimizationAutoscalerBehaviorFormModel => {
  const mapDirection = (
    direction?: WorkloadOptimizationHorizontalPodAutoscalerBehavior['scaleUp']
  ): WorkloadOptimizationAutoscalerBehaviorFormModel['scaleUp'] => {
    const pods = direction?.policies?.find(
      (p) => p.type === HPAScalingPolicyType.PODS_SCALING_POLICY
    );
    const percent = direction?.policies?.find(
      (p) => p.type === HPAScalingPolicyType.PERCENT_SCALING_POLICY
    );
    return {
      stabilizationWindowSeconds: direction?.stabilizationWindowSeconds ?? null,
      pods: pods
        ? { value: pods.value, periodSeconds: pods.periodSeconds }
        : null,
      percent: percent
        ? { value: percent.value, periodSeconds: percent.periodSeconds }
        : null,
    };
  };

  return {
    scaleUp: mapDirection(behavior?.scaleUp),
    scaleDown: mapDirection(behavior?.scaleDown),
  };
};

export const mapFormModelAutoscalerBehaviorToApi = (
  autoscalerBehavior: WorkloadOptimizationAutoscalerBehaviorFormModel
): WorkloadOptimizationHorizontalPodAutoscalerBehavior => {
  const buildPolicies = (
    scaling: WorkloadOptimizationAutoscalerBehaviorFormModel['scaleUp']
  ): NonNullable<
    NonNullable<
      WorkloadOptimizationHorizontalPodAutoscalerBehavior['scaleUp']
    >['policies']
  > =>
    [
      scaling.pods && {
        type: HPAScalingPolicyType.PODS_SCALING_POLICY,
        value: scaling.pods.value!,
        periodSeconds: scaling.pods.periodSeconds!,
      },
      scaling.percent && {
        type: HPAScalingPolicyType.PERCENT_SCALING_POLICY,
        value: scaling.percent.value!,
        periodSeconds: scaling.percent.periodSeconds!,
      },
    ].filter((v) => v !== null);

  return {
    scaleUp: {
      stabilizationWindowSeconds:
        autoscalerBehavior.scaleUp.stabilizationWindowSeconds,
      policies: buildPolicies(autoscalerBehavior.scaleUp),
    },
    scaleDown: {
      stabilizationWindowSeconds:
        autoscalerBehavior.scaleDown.stabilizationWindowSeconds,
      policies: buildPolicies(autoscalerBehavior.scaleDown),
    },
  };
};

export const isEnabledAutoAssignmentPolicy = ({
  workload,
}: {
  workload: OptimizationWorkload;
}): boolean => workload.scalingPolicyOrigin === 'ORIGIN_ASSIGNMENT_RULES';

export const scalingPolicyHasHpaSettings = (
  scalingPolicy: WorkloadScalingPolicy
): boolean => {
  return !!scalingPolicy.hpaSettings?.nativeHpaSpec;
};

export const workloadOwnershipWasTaken = (
  workload: PreparedWorkload
): boolean => {
  return (
    !!workload.workloadConfigV2.hpaConfig.takeOwnership &&
    workload.workloadConfigV2.hpaConfig.managementOption === 'MANAGED'
  );
};

// checks only if exists hpaV2 settings, not if applicable, or active
export const workloadHasHpaV2Settings = (
  workload: PreparedWorkload
): boolean => {
  const {
    workloadConfigV2: {
      hpaConfig: { useNative },
    },
  } = workload;

  return !!useNative;
};

export const isHpaV2Unavailable = (
  workload?: Pick<OptimizationWorkload, 'nativeHpaUnsupportedReasonDetails'>
): boolean => !!workload?.nativeHpaUnsupportedReasonDetails;

export const isHpaV2UnavailableOnWorkloadType = (
  workload?: Pick<OptimizationWorkload, 'nativeHpaUnsupportedReasonDetails'>
): boolean =>
  workload?.nativeHpaUnsupportedReasonDetails?.type ===
  HPAUnsupportedReasonTypeEnum.WORKLOAD_TYPE;

export const isHpaV2UnavailableOnTakeOwnership = (
  workload?: Pick<OptimizationWorkload, 'nativeHpaUnsupportedReasonDetails'>
): boolean =>
  workload?.nativeHpaUnsupportedReasonDetails?.type ===
  HPAUnsupportedReasonTypeEnum.TAKE_OWNERSHIP;

// todo:mike re check after new hpa be implementation is deployed UI-6263
export const hpaExists = (
  hpaState: NonNullable<OptimizationWorkload['hpaState']>
): boolean => {
  return (
    hpaState.mode === 'HPA_MODE_LEGACY' ||
    hpaState.mode === 'HPA_MODE_TAKEOVER' ||
    hpaState.mode === 'HPA_MODE_V2'
  );
};
